import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Badge, Input } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { ArrowDown, ArrowUp, Search } from 'lucide-react';
import { memo, useMemo, useState } from 'react';
const optionTypeLabels = {
    base: 'Base',
    option_period: 'Option',
    optional_task: 'Optional'
};
const formatPrice = (value)=>{
    if (!value) return '-';
    const num = Number(value);
    if (Number.isNaN(num)) return value;
    return `$${num.toLocaleString()}`;
};
export const CLINsTable = /*#__PURE__*/ memo(function CLINsTable({ clins, onRowClick }) {
    const [sortField, setSortField] = useState('clinNumber');
    const [sortOrder, setSortOrder] = useState('asc');
    const [filters, setFilters] = useState({
        search: '',
        optionType: 'all',
        contractType: 'all'
    });
    // Handle sort
    const handleSort = (field)=>{
        if (sortField === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortOrder('asc');
        }
    };
    // Filter and sort CLINs
    const filteredAndSortedCLINs = useMemo(()=>{
        let result = [
            ...clins
        ];
        // Apply filters
        if (filters.search) {
            const searchLower = filters.search.toLowerCase();
            result = result.filter((clin)=>clin.clinNumber?.toLowerCase().includes(searchLower) || clin.title?.toLowerCase().includes(searchLower) || clin.description?.toLowerCase().includes(searchLower));
        }
        if (filters.optionType !== 'all') {
            result = result.filter((clin)=>clin.optionType === filters.optionType);
        }
        if (filters.contractType !== 'all') {
            result = result.filter((clin)=>clin.contractType === filters.contractType);
        }
        // Apply sort
        result.sort((a, b)=>{
            let aValue;
            let bValue;
            switch(sortField){
                case 'clinNumber':
                    aValue = a.clinNumber || '';
                    bValue = b.clinNumber || '';
                    break;
                case 'title':
                    aValue = a.title || '';
                    bValue = b.title || '';
                    break;
                case 'optionType':
                    aValue = a.optionType || '';
                    bValue = b.optionType || '';
                    break;
                case 'contractType':
                    aValue = a.contractType || '';
                    bValue = b.contractType || '';
                    break;
                case 'quantity':
                    aValue = Number(a.pricing?.quantity) || 0;
                    bValue = Number(b.pricing?.quantity) || 0;
                    break;
                case 'unitPrice':
                    aValue = Number(a.pricing?.unitPrice) || 0;
                    bValue = Number(b.pricing?.unitPrice) || 0;
                    break;
                case 'extendedPrice':
                    aValue = Number(a.pricing?.extendedPrice) || 0;
                    bValue = Number(b.pricing?.extendedPrice) || 0;
                    break;
                default:
                    aValue = a.clinNumber || '';
                    bValue = b.clinNumber || '';
            }
            if (typeof aValue === 'string' && typeof bValue === 'string') {
                return sortOrder === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
            }
            return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
        });
        return result;
    }, [
        clins,
        filters,
        sortField,
        sortOrder
    ]);
    const SortIcon = ({ field })=>{
        if (sortField !== field) return null;
        return sortOrder === 'asc' ? /*#__PURE__*/ _jsx(ArrowUp, {
            className: "w-4 h-4 ml-1 inline"
        }) : /*#__PURE__*/ _jsx(ArrowDown, {
            className: "w-4 h-4 ml-1 inline"
        });
    };
    // Get unique contract types from data for filter dropdown
    const contractTypes = useMemo(()=>{
        const types = new Set(clins.map((c)=>c.contractType).filter(Boolean));
        return Array.from(types).sort();
    }, [
        clins
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "px-6 py-4 border-b border-gray-200 bg-gray-50",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "grid grid-cols-1 md:grid-cols-3 gap-4",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "relative",
                            children: [
                                /*#__PURE__*/ _jsx(Search, {
                                    className: "absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4"
                                }),
                                /*#__PURE__*/ _jsx(Input, {
                                    type: "text",
                                    placeholder: "Search CLINs...",
                                    value: filters.search,
                                    onChange: (e)=>setFilters({
                                            ...filters,
                                            search: e.target.value
                                        }),
                                    className: "pl-10"
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("select", {
                            value: filters.optionType,
                            onChange: (e)=>setFilters({
                                    ...filters,
                                    optionType: e.target.value
                                }),
                            className: "px-3 py-2 border border-gray-300 rounded-md text-sm",
                            children: [
                                /*#__PURE__*/ _jsx("option", {
                                    value: "all",
                                    children: "All Option Types"
                                }),
                                /*#__PURE__*/ _jsx("option", {
                                    value: "base",
                                    children: "Base"
                                }),
                                /*#__PURE__*/ _jsx("option", {
                                    value: "option_period",
                                    children: "Option Period"
                                }),
                                /*#__PURE__*/ _jsx("option", {
                                    value: "optional_task",
                                    children: "Optional Task"
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("select", {
                            value: filters.contractType,
                            onChange: (e)=>setFilters({
                                    ...filters,
                                    contractType: e.target.value
                                }),
                            className: "px-3 py-2 border border-gray-300 rounded-md text-sm",
                            children: [
                                /*#__PURE__*/ _jsx("option", {
                                    value: "all",
                                    children: "All Contract Types"
                                }),
                                contractTypes.map((type)=>/*#__PURE__*/ _jsx("option", {
                                        value: type,
                                        children: type
                                    }, type))
                            ]
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "overflow-x-auto",
                children: /*#__PURE__*/ _jsxs("table", {
                    className: "w-full",
                    children: [
                        /*#__PURE__*/ _jsx("thead", {
                            className: "bg-gray-50 border-b border-gray-200",
                            children: /*#__PURE__*/ _jsxs("tr", {
                                children: [
                                    /*#__PURE__*/ _jsxs("th", {
                                        className: "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100",
                                        onClick: ()=>handleSort('clinNumber'),
                                        children: [
                                            "CLIN #",
                                            /*#__PURE__*/ _jsx(SortIcon, {
                                                field: "clinNumber"
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("th", {
                                        className: "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100",
                                        onClick: ()=>handleSort('title'),
                                        children: [
                                            "Title",
                                            /*#__PURE__*/ _jsx(SortIcon, {
                                                field: "title"
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("th", {
                                        className: "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100",
                                        onClick: ()=>handleSort('optionType'),
                                        children: [
                                            "Type",
                                            /*#__PURE__*/ _jsx(SortIcon, {
                                                field: "optionType"
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("th", {
                                        className: "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100",
                                        onClick: ()=>handleSort('contractType'),
                                        children: [
                                            "Contract",
                                            /*#__PURE__*/ _jsx(SortIcon, {
                                                field: "contractType"
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx("th", {
                                        className: "px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider",
                                        children: "PoP"
                                    }),
                                    /*#__PURE__*/ _jsxs("th", {
                                        className: "px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100",
                                        onClick: ()=>handleSort('quantity'),
                                        children: [
                                            "Qty",
                                            /*#__PURE__*/ _jsx(SortIcon, {
                                                field: "quantity"
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("th", {
                                        className: "px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100",
                                        onClick: ()=>handleSort('unitPrice'),
                                        children: [
                                            "Unit Price",
                                            /*#__PURE__*/ _jsx(SortIcon, {
                                                field: "unitPrice"
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("th", {
                                        className: "px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100",
                                        onClick: ()=>handleSort('extendedPrice'),
                                        children: [
                                            "Extended",
                                            /*#__PURE__*/ _jsx(SortIcon, {
                                                field: "extendedPrice"
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx("th", {
                                        className: "px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider",
                                        children: "Ceiling (NTE)"
                                    }),
                                    /*#__PURE__*/ _jsx("th", {
                                        className: "px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider",
                                        children: "Source"
                                    })
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsx("tbody", {
                            className: "bg-white divide-y divide-gray-200",
                            children: filteredAndSortedCLINs.map((clin)=>/*#__PURE__*/ _jsxs("tr", {
                                    "data-testid": `${Testids.clin.tableRow}-${clin.clinNumber}`,
                                    onClick: ()=>onRowClick(clin),
                                    className: "hover:bg-gray-50 cursor-pointer transition-colors",
                                    children: [
                                        /*#__PURE__*/ _jsx("td", {
                                            className: "px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900",
                                            children: /*#__PURE__*/ _jsxs("div", {
                                                className: "flex items-center gap-2",
                                                children: [
                                                    clin.clinNumber,
                                                    clin.parentClin && /*#__PURE__*/ _jsxs("span", {
                                                        className: "text-xs text-gray-400",
                                                        children: [
                                                            "sub of ",
                                                            clin.parentClin
                                                        ]
                                                    }),
                                                    clin.correspondingBaseClin && !clin.parentClin && /*#__PURE__*/ _jsxs("span", {
                                                        className: "text-xs text-gray-400",
                                                        children: [
                                                            "base: ",
                                                            clin.correspondingBaseClin
                                                        ]
                                                    })
                                                ]
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            className: "px-6 py-4 text-sm text-gray-900",
                                            children: /*#__PURE__*/ _jsx("div", {
                                                className: "max-w-xs truncate",
                                                children: clin.title
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            className: "px-6 py-4 whitespace-nowrap text-sm text-gray-500",
                                            children: clin.optionType ? /*#__PURE__*/ _jsxs("div", {
                                                className: "flex flex-col gap-0.5",
                                                children: [
                                                    /*#__PURE__*/ _jsxs("div", {
                                                        className: "flex items-center gap-1",
                                                        children: [
                                                            /*#__PURE__*/ _jsx(Badge, {
                                                                variant: "outline",
                                                                children: optionTypeLabels[clin.optionType] || clin.optionType
                                                            }),
                                                            clin.isOptionalTask && /*#__PURE__*/ _jsxs(Badge, {
                                                                variant: "secondary",
                                                                className: "bg-orange-100 text-orange-700 border-orange-200 text-[10px] px-1 py-0",
                                                                children: [
                                                                    "OT",
                                                                    clin.optionalTaskNumber || ''
                                                                ]
                                                            })
                                                        ]
                                                    }),
                                                    clin.periodName && /*#__PURE__*/ _jsx("span", {
                                                        className: "text-xs text-gray-400",
                                                        children: clin.periodName
                                                    })
                                                ]
                                            }) : '-'
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            className: "px-6 py-4 whitespace-nowrap text-sm text-gray-500",
                                            children: /*#__PURE__*/ _jsxs("div", {
                                                className: "flex items-center gap-1",
                                                children: [
                                                    clin.contractType ? /*#__PURE__*/ _jsx(Badge, {
                                                        variant: "outline",
                                                        children: clin.contractType
                                                    }) : '-',
                                                    clin.needsTeamPricing && /*#__PURE__*/ _jsx(Badge, {
                                                        variant: "secondary",
                                                        className: "bg-indigo-100 text-indigo-700 border-indigo-200 text-[10px] px-1 py-0",
                                                        children: "Team"
                                                    })
                                                ]
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            className: "px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center",
                                            children: clin.exerciseInfo?.popMonths ? /*#__PURE__*/ _jsxs("div", {
                                                className: "flex flex-col items-center gap-0.5",
                                                children: [
                                                    /*#__PURE__*/ _jsxs("span", {
                                                        children: [
                                                            clin.exerciseInfo.popMonths,
                                                            " mo"
                                                        ]
                                                    }),
                                                    clin.exerciseInfo.farClause && /*#__PURE__*/ _jsx(Badge, {
                                                        variant: "secondary",
                                                        className: "text-[10px] px-1 py-0 font-mono bg-slate-100 text-slate-600",
                                                        children: clin.exerciseInfo.farClause
                                                    })
                                                ]
                                            }) : '-'
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            className: "px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right",
                                            children: clin.pricing?.quantity || '-'
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            "data-testid": `${Testids.clin.tableRowUnitPriceCell}-${clin.clinNumber}`,
                                            className: "px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right",
                                            children: formatPrice(clin.pricing?.unitPrice)
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            "data-testid": `${Testids.clin.tableRowExtendedPriceCell}-${clin.clinNumber}`,
                                            className: "px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right",
                                            children: formatPrice(clin.pricing?.extendedPrice)
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            className: "px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right",
                                            children: clin.pricing?.ceiling ? formatPrice(clin.pricing.ceiling) : '-'
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            className: "px-6 py-4 whitespace-nowrap text-xs text-gray-400 text-center",
                                            children: clin.source?.filename ? /*#__PURE__*/ _jsx("span", {
                                                title: clin.source.filename,
                                                className: "truncate max-w-[100px] inline-block",
                                                children: clin.source.filename
                                            }) : '-'
                                        })
                                    ]
                                }, `${clin.clinNumber}-${clin.sourceDocumentId || ''}-${clin.amendmentNumber || ''}`))
                        })
                    ]
                })
            }),
            filteredAndSortedCLINs.length === 0 && /*#__PURE__*/ _jsx("div", {
                className: "px-6 py-12 text-center text-gray-500",
                children: "No CLINs match the current filters"
            })
        ]
    });
});
